// MovementTable — THE ONE WAY MONEY MOVEMENTS ARE LISTED, wherever they are.
//
// Extracted from screens/Movements.tsx on 18 Sept 2026 (operator, on Home:
// "NEED RECENT MONEY MOVEMENTS HERE AS WELL"): the currency blocks, the day
// rows, the per-leg lines, the Description builder and the Account, Movement,
// Balance and Reference cells were the Money screen's own, and Home wanted
// the same rows. A second copy is how two screens start describing one entry
// two ways, so the rendering moved here whole — same DOM, same classes, same
// keys, same doors — and Money movements consumes it with everything on.
//
// WHAT IT DRAWS. One block per currency, largest net first (by the totals over
// the whole filtered set when they are given; a currency without a total
// stands last). Each block: a LedgerHead carrying the closing balance with
// the opening and the in and out beside it (the net when the Worker sends no
// closing; the currency alone when no totals are given), then a table of this
// page's lines in that currency under their day rows:
//   When · Type · Description · (Account) · Movement · (Balance) · Reference · (Reverse)
// A transaction stands in the block of EACH asset it has a leg in (the
// Worker's `legs`); a railless row — between parties, a party opening, a
// profit share — stands once, in its head asset, with a dash for the movement
// and no balance. The BFF's full sentence and the note ride on the row's
// tooltip. The header of screens/Movements.tsx says why each column reads as
// it does; nothing there changed when the code moved.
//
// THE PHONE FORM (19 Sept 2026, operator: "fix the phone layout for the home
// screen too"). Below lg the table is hidden and each block draws CARDS on
// Ledger's card seats (the party statement's LegCard is the model): the same
// LedgerHead, a day label, then one card per line — line 1 the type pill and
// the whole description (two lines of it at most) with the account muted
// after it, and the reference at the line's right end (a deal opens, a
// receipt or payment copies — the row's door, on every card); line 2 the
// time (the day label names the day), the movement, the bare Reverse word
// where the door is on, and the balance where a Balance column would stand.
// The reverse panel opens beneath the card as it does beneath the row. The
// table at lg+ is untouched; the two forms are switched by the breakpoint
// alone, so Money and Home pick the cards up with no change of their own.
//
// WHAT THE CALLER DECIDES — the props:
//   rows         the movements, newest first, as the route sends them
//   totals       the route's totals over the whole filtered set; omit them
//                and the block header is the currency alone
//   entity       the desk the rows were read for (a party link opens on the
//                party's own desk where the row says one; this is the fallback)
//   showAccount  the Account column. Money drops it when the account filter
//                names one account — every row would repeat the same words —
//                and then passes that account's name as `accountName`, which
//                joins the block title ("AED · Office cash")
//   showBalance  the Balance column — Money seats it once a row carries `legs`
//                (an older Worker sends none, and a column of blanks would
//                read as "no balance")
//   showReverse  the Reverse seat at the row's end and the panel beneath the
//                row (components/ReverseEntry). Off on a glance (Home); the
//                door is on Money
//   dealsById    the trades log's page by deal id — a row that names a deal
//                on that page opens it through `onOpenDeal`; one that does not
//                is named and does not pretend to open
//   onReversed   where the screen seats the outcome; needed only with the door
//   compact      a tighter block (no top margin above the first header) for a
//                card that already has a header line of its own; the block
//                also drops Ledger's 1240px cap, so on Home it ends on the
//                same edge as the Recent trades table above it

import { Fragment, useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { CopyRef } from './CopyRef';
import { lifecycleLabel, isChequeDeal } from './DealSlideOver';
import { LEDGER, KindPill, LedgerHead, DayRow, Delta, Figure, rateQuote } from './Ledger';
import {
  ReverseEntryButton, ReverseEntryPanel, canReverse, type ReversedOutcome,
} from './ReverseEntry';
import type { MovementRow, MovementTotal, MovementRail, TradeRow, DealRow } from '../lib/api';
import { displayAsset, fmtAmount, fmtRate, fmtTime, fmtWeekday, dateBucket, dubaiDate } from '../lib/format';
import { partyName, railName, foldAsset, movementKindLabel, shortReference } from '../lib/naming';

/** A movement row, plus the deal its account leg settled. Optional, so a row
 *  without one — every kind but a trade or a cheque — satisfies it unchanged.
 *  `linked_deal_id` (1 Oct 2026, bff/src/routes/movements.ts) is the same
 *  fact for a row that is NOT its own leg — a sub-broker's money-in or
 *  delivery-out — kept a separate field from `deal_id` so the Reverse door
 *  never mistakes an ordinary, reversible transfer for a trade's own leg
 *  (see journalEntries(), the only place the two are folded into one). */
export type MovementLineRow = MovementRow & { deal_id?: string | null; linked_deal_id?: string | null };

/** ONE LINE OF THE STATEMENT: a transaction, in one currency. With the
 *  Worker's `legs` a transaction yields one line per asset it moved on the
 *  accounts in scope, each carrying that leg's own signed net and the balance
 *  after it; a row without legs — railless, or from an older Worker — yields
 *  one line in its head asset with `net` and `running` null, and the line
 *  then draws the movement the row always carried and no balance. */
export interface StatementLine {
  /** txn_id + asset — a transaction stands once per currency. */
  key: string;
  m: MovementLineRow;
  /** Folded (lib/naming foldAsset) — the block it stands in. */
  asset: string;
  net: number | null;
  running: number | null;
}

/** One currency's lines on this page, and its total over the whole filtered
 *  set (null only for a currency the totals do not name). */
export interface CurrencyBlock { asset: string; lines: StatementLine[]; total: MovementTotal | null }

/** Nine places, past every scale in the asset registry — so folding two raw
 *  codes into one figure cannot reintroduce a float tail. */
export const tidy = (n: number) => Number(n.toFixed(9));

/** The lines a row yields (see StatementLine). Legs fold by asset: two raw
 *  codes that fold to one currency are one line, their nets and balances
 *  summed — the same fold the route applies to the totals. */
export function linesOf(m: MovementLineRow): StatementLine[] {
  if (Array.isArray(m.legs) && m.legs.length > 0) {
    const byAsset = new Map<string, { net: number; running: number | null }>();
    for (const l of m.legs) {
      const a = foldAsset(l.asset);
      const cur = byAsset.get(a) ?? { net: 0, running: null };
      cur.net = tidy(cur.net + Number(l.net));
      if (l.running !== null && l.running !== undefined) cur.running = tidy((cur.running ?? 0) + Number(l.running));
      byAsset.set(a, cur);
    }
    return [...byAsset.entries()].map(([asset, v]) => ({
      key: `${m.txn_id}|${asset}`, m, asset, net: v.net, running: v.running,
    }));
  }
  const asset = foldAsset(m.asset);
  return [{ key: `${m.txn_id}|${asset}`, m, asset, net: null, running: null }];
}

/** ONE BLOCK PER CURRENCY (17 Sept 2026). A transaction stands in the block
 *  of every asset it has a leg in; the lines keep the route's order — newest
 *  first — inside their block; the blocks stand largest net first, by the
 *  totals over the whole filtered set. A currency that has rows but no total
 *  stands last. With one asset asked for there is one block. */
export function currencyBlocks(rows: MovementLineRow[], totals: MovementTotal[]): CurrencyBlock[] {
  const byAsset = new Map<string, StatementLine[]>();
  for (const m of rows) {
    for (const line of linesOf(m)) {
      const list = byAsset.get(line.asset);
      if (list) list.push(line); else byAsset.set(line.asset, [line]);
    }
  }
  const totalOf = new Map(totals.map((t) => [foldAsset(t.asset), t]));
  return [...byAsset.entries()]
    .map(([asset, lines]) => ({ asset, lines, total: totalOf.get(asset) ?? null }))
    .sort((a, b) => {
      const na = a.total ? Math.abs(a.total.net) : -1;
      const nb = b.total ? Math.abs(b.total.net) : -1;
      return nb - na || a.asset.localeCompare(b.asset);
    });
}

/** The reference column: a deal id or a receipt/payment reference, SHORT —
 *  "DEAL 8W5EJ", "PAY HNL4L" (shortReference; the day is the When column,
 *  the full id is the tooltip and the copy). Left-aligned so the ids stack
 *  on one edge. The Reverse door is NOT in it — it has LEDGER.act, a seat
 *  of its own on every row, so it sits at one x down the block (reviewer,
 *  17 Sept 2026: it floated after the variable-width id). 18 Sept: the full
 *  13rem id clipped and scrolled the table sideways at the operator's width. */
const REF = 'w-[7.5rem] min-w-[7.5rem] pl-2 whitespace-nowrap text-meta text-tg-hint';
/** The account column: bounded, so a long account name cannot push the
 *  figures away — the description is the one column that gives way. */
const ACCOUNT = 'w-[10rem] min-w-[10rem] max-w-[10rem]';

/** The day a row sits under — Dubai's calendar, the same words as the party
 *  statement's separators (Today / Yesterday / the weekday). */
export function dayKey(m: MovementLineRow): string {
  return dubaiDate(m.at) || 'undated';
}
export function dayLabel(m: MovementLineRow): string {
  if (!m.at) return 'Undated';
  const b = dateBucket(m.at);
  return b === 'today' ? 'Today' : b === 'yesterday' ? 'Yesterday' : fmtWeekday(m.at);
}

const NO_TOTALS: MovementTotal[] = [];
const noop = () => {};

export interface MovementTableProps {
  rows: MovementLineRow[];
  totals?: MovementTotal[];
  entity: string;
  showAccount: boolean;
  showBalance: boolean;
  showReverse: boolean;
  /** The one account's name when the table is that account's own statement
   *  (Money's account filter): joins each block's title. Null otherwise. */
  accountName?: string | null;
  dealsById: Map<string, TradeRow>;
  onOpenDeal: (dealId: string) => void;
  onReversed?: (o: ReversedOutcome) => void;
  compact?: boolean;
  /** The "Today" / "Yesterday" / weekday rows between days. Off when the window
   *  is one day and its chip already says which (Money's Today: the label sat
   *  at the top of every one of six blocks — 21 Oct 2026 audit). */
  dayRows?: boolean;
}

/** The currency blocks, one after another — the caller owns the scroller
 *  around them, the empty state and the paging beneath. */
export function MovementTable({
  rows, totals = NO_TOTALS, entity, showAccount, showBalance, showReverse,
  accountName = null, dealsById, onOpenDeal, onReversed = noop, compact = false, dayRows = true,
}: MovementTableProps) {
  const blocks = useMemo(() => currencyBlocks(rows, totals), [rows, totals]);
  return (
    <>
      {blocks.map((b) => (
        <CurrencyLedger
          key={b.asset}
          block={b}
          entity={entity}
          showAccount={showAccount}
          showBalance={showBalance}
          showReverse={showReverse}
          accountName={accountName}
          dealsById={dealsById}
          onOpenDeal={onOpenDeal}
          onReversed={onReversed}
          compact={compact}
          dayRows={dayRows}
        />
      ))}
    </>
  );
}

/** ONE CURRENCY, ONE BLOCK — the statement's header and its table. The
 *  header carries the closing balance when the Worker sends it, with the
 *  opening and the in and out beside it; on an older Worker, the net with
 *  the in and out — a move counts toward neither side, the same money on
 *  both would invent money the desk never had. Without totals, the currency
 *  alone. The table beneath is this page's lines in that currency, under
 *  their day rows: When · Type · Description · Account · Movement · Balance ·
 *  Reference · (Reverse). */
export function CurrencyLedger({
  block, entity, showAccount, showBalance, showReverse, accountName, dealsById, onOpenDeal, onReversed, compact = false, dayRows = true,
}: {
  block: CurrencyBlock;
  entity: string;
  showAccount: boolean;
  showBalance: boolean;
  showReverse: boolean;
  accountName: string | null;
  dealsById: Map<string, TradeRow>;
  onOpenDeal: (dealId: string) => void;
  onReversed: (o: ReversedOutcome) => void;
  compact?: boolean;
  dayRows?: boolean;
}) {
  const t = block.total;
  const closing = t?.closing !== undefined && t.closing !== null ? Number(t.closing) : null;
  const opening = t?.opening !== undefined && t.opening !== null ? Number(t.opening) : null;
  const inOut = t ? `in ${fmtAmount(t.in, block.asset)} · out ${fmtAmount(t.out, block.asset)}` : '';
  // Five fixed seats — When · Type · Description · Movement · Reference —
  // plus Account, Balance and Reverse when they stand. The day rows and the
  // reverse panel span all of them, the Reverse seat included.
  const cols = 6 + (showAccount ? 1 : 0) + (showBalance ? 1 : 0) + (showReverse ? 1 : 0);   // the seats and the slack column
  return (
    <section className={`${compact ? '' : LEDGER.block} px-2 ${compact ? 'pt-2' : 'pt-3'} pb-1`}>
      <LedgerHead
        title={accountName ? `${displayAsset(block.asset)} · ${accountName}` : displayAsset(block.asset)}
        figure={t
          ? (closing !== null
            // "closing" in words (21 Oct 2026 audit): opening, in and out each had
            // one, so the bare figure read as a net or a total of the lines below.
            ? <><span className="font-sans font-normal text-tg-hint">closing </span><Figure value={closing} asset={block.asset} /></>
            : <Delta value={t.net} asset={block.asset} />)
          : undefined}
        note={t
          ? (opening !== null ? `opening ${fmtAmount(opening, block.asset)} · ${inOut}` : inOut)
          : undefined}
      />
      {/* No sticky column line: the screen is several blocks, and a line
          that stayed put once the block's own header had scrolled away
          would name columns for a currency the eye could not see. The
          column seats carry their own minimum widths; this floor only
          keeps a short block from collapsing on a narrow window. */}
      {/* THE FLOOR IS THE SIZED COLUMNS PLUS ROOM FOR THE DESCRIPTION. Under
          table-fixed the one unsized column gets what the sized ones leave —
          below this floor it got nothing (18 Sept 2026), so a narrow window
          scrolls the statement sideways rather than losing its words. */}
      {/* The table at lg+ only — `lg:table` is a table's own display, so
          the lg+ form is the one it always was; below lg the cards stand. */}
      <table className={`hidden lg:table ${LEDGER.table} ${showAccount ? 'min-w-[1160px]' : 'min-w-[900px]'}`}>
        <thead>
          <tr>
            <th className={`${LEDGER.th} ${LEDGER.whenW}`}>When</th>
            <th className={`${LEDGER.th} ${LEDGER.kind}`}>Type</th>
            <th className={`${LEDGER.th} ${LEDGER.words}`}>Description</th>
            {showAccount && <th className={`${LEDGER.th} ${ACCOUNT}`}>Account</th>}
            <th className={`${LEDGER.th} ${LEDGER.fig}`}>Movement</th>
            {showBalance && <th className={`${LEDGER.th} ${LEDGER.fig}`}>Balance</th>}
            <th className={`${LEDGER.th} ${REF}`}>Reference</th>
            {showReverse && <th className={`${LEDGER.th} ${LEDGER.act}`} aria-label="Reverse" />}
            <th className={LEDGER.slack} aria-hidden="true" />
          </tr>
        </thead>
        <tbody>
          {block.lines.map((line, i) => {
            const prev = i > 0 ? block.lines[i - 1]?.m ?? null : null;
            const newDay = !prev || dayKey(prev) !== dayKey(line.m);
            return (
              <Fragment key={line.key}>
                {dayRows && newDay && <DayRow label={dayLabel(line.m)} colSpan={cols} />}
                <MovementLine
                  entity={entity}
                  line={line}
                  showAccount={showAccount}
                  showBalance={showBalance}
                  showReverse={showReverse}
                  cols={cols}
                  // Offered only when the deal is in hand. The trades log
                  // holds one page, so a deal older than it is named on the
                  // row and does not pretend to open.
                  onOpenDeal={line.m.deal_id && dealsById.has(line.m.deal_id) ? onOpenDeal : null}
                  onReversed={onReversed}
                />
              </Fragment>
            );
          })}
        </tbody>
      </table>
      {/* THE PHONE FORM: the same lines as cards, under the same day labels. */}
      <div className="lg:hidden">
        {block.lines.map((line, i) => {
          const prev = i > 0 ? block.lines[i - 1]?.m ?? null : null;
          const newDay = !prev || dayKey(prev) !== dayKey(line.m);
          return (
            <Fragment key={line.key}>
              {dayRows && newDay && <div className={LEDGER.day}>{dayLabel(line.m)}</div>}
              <MovementCard
                entity={entity}
                line={line}
                showAccount={showAccount}
                showBalance={showBalance}
                showReverse={showReverse}
                onOpenDeal={line.m.deal_id && dealsById.has(line.m.deal_id) ? onOpenDeal : null}
                onReversed={onReversed}
              />
            </Fragment>
          );
        })}
      </div>
    </section>
  );
}

/** An account, named the way every other screen names it: the stored label
 *  wins, and the stored id is only ever the fallback the grammar cleans up. */
export function railWords(r: MovementRail | null): string {
  return r ? railName({ slug: r.slug, label: r.label }) : '';
}

/** A cost category in the desk's words — the same spelling the BFF's own
 *  sentence uses (lib/movements.ts COST_WORDS), so the Description column
 *  and the tooltip never name one cost two ways. Lower case; the expense
 *  description capitalises it. */
const COST_WORDS: Readonly<Record<string, string>> = {
  'travel':     'travel or transport',
  'angadiya':       'angadiya commission',
  'salary':         'salary',
  'office:rental':  'rent',
  'office':         'office or admin cost',
  'office:domains': 'domains',
  'network_fee':    'network fee',
  'bank':           'bank or card charge',
  'professional':   'legal, licence or professional fee',
  'other':          'other cost',
};
export function costWords(category: string | null | undefined): string {
  if (!category) return 'cost';
  return COST_WORDS[category] ?? category.replace(/[:_]/g, ' ');
}
const capitalise = (w: string) => w.charAt(0).toUpperCase() + w.slice(1);

/** An opening balance carries no kind of its own — on an account it is
 *  money_in / money_out, on a party an adjustment — and the row does not
 *  carry the ledger type; the BFF's sentence is the one place it is named. */
export const isOpening = (m: MovementLineRow) => /^(reversal — )?opening balance\b/i.test(m.words ?? '');
/** The desk's profit share is filed as an adjustment (bff lib/movements.ts
 *  RAILLESS_MOVEMENTS) and, like an opening, named only in the sentence. */
export const isProfitShare = (m: MovementLineRow) => /^(reversal — )?desk's profit share\b/i.test(m.words ?? '');
/** A row the Worker sent legs for and that moved NOTHING on the accounts in
 *  scope — between parties, a party opening, a profit share, or an entry whose
 *  account sits on the other desk. The Movement cell says the amount that
 *  crossed, unsigned and muted (crossedFigure) — a phone has no tooltip to read
 *  it from. An older Worker sends no `legs` at all, and that row keeps its
 *  headline. */
export const railless = (m: MovementLineRow) => Array.isArray(m.legs) && m.legs.length === 0;

/** A RAILLESS ROW'S FIGURE. Nothing moved on a desk account, so the Movement
 *  seat was a dash and the amount rode the tooltip sentence — which a phone
 *  does not have (the operator, 14 Oct 2026, on Home's journal: a
 *  between-parties row that named two parties and no amount; the 21 Oct audit
 *  found the same dash on a party's opening balance and the desk's profit
 *  share). It stands in the Movement seat, where "how much moved" is read,
 *  drawn as every amount that crossed without being in or out is: unsigned and
 *  muted (MovementFigure). Beside the names was tried first and failed on the
 *  phone card — the pill leaves the names two lines, and the figure fell off
 *  the card's clamp.
 *
 *  Leg 1 is the row's own amount. A between-parties SWAP's return leg travels
 *  only in the sentence the server wrote ("swapped … for 18,380 AED back"), so
 *  it is read from there — only from a sentence that says "swapped", so a
 *  one-leg move to a party with "for … back" in its name is not taken for one —
 *  and left out, never guessed, when the sentence does not say it.
 *  null when the row has no figure to print. */
export function crossedFigure(m: MovementLineRow): { amount: string; back: string | null } | null {
  if (!m.asset || !(m.amount > 0)) return null;
  const back = m.kind === 'p2p' ? (/^(?:reversal — )?swapped .* for (.+) back$/.exec(m.words ?? '')?.[1] ?? null) : null;
  return { amount: fmtAmount(m.amount, m.asset), back };
}

/** THE ACCOUNT OF ONE LINE — one account, never a pair (the pair is the
 *  Description's, as "To …" / "From …"). On a move: the account that
 *  received for the leg that gained money, else the account that paid — the
 *  paying side also for a leg that netted to nothing because both sides were
 *  in scope, and for a row without legs. On a trade: the head asset's line
 *  names `rail`, the other asset's line names `other_rail` (the Worker sends
 *  it since 17 Sept 2026; before that the line shows nothing rather than the
 *  wrong account). Every other kind: the row's account.
 *
 *  THE DESK'S OWN SWAP stands on ONE account — USDC to USDT on KuCoin — and the
 *  route sends `other_rail` only when the second account DIFFERS from `rail`
 *  (bff routes/movements.ts: on a one-sided transaction both fields resolve to
 *  the same account, and the sentence would lie). So on a party-less trade a
 *  second line that did move money and has no `other_rail` is on `rail`: the
 *  line names it instead of a dash. A customer's trade is left alone — its
 *  second account is a different one, and a missing name there is missing. */
export function lineAccount(line: StatementLine): MovementRail | null {
  const m = line.m;
  if (m.kind === 'rail_move') return line.net !== null && line.net > 0 && m.counter_rail ? m.counter_rail : m.rail;
  if (m.kind === 'trade') {
    if (line.asset === foldAsset(m.asset)) return m.rail;
    return m.other_rail ?? (!m.party && line.net !== null ? m.rail : null);
  }
  return m.rail;
}

/** A TRADE WITH NO PARTY is the desk converting one currency into another on
 *  its own accounts — a swap on an exchange. The Wio statement writes a
 *  currency exchange as "AED to EUR" (Movements.tsx, 17 Sept 2026): the
 *  currency that left, then the one that came, and never an amount inside.
 *  Null when the legs cannot say — an older Worker sends none, a railless row
 *  has none, two raw codes fold into one currency — and the Description then
 *  names the account rather than print a dash. */
export function swapWords(m: MovementLineRow): string | null {
  const lines = linesOf(m);
  const gave = lines.filter((l) => l.net !== null && l.net < 0).map((l) => displayAsset(l.asset));
  const got  = lines.filter((l) => l.net !== null && l.net > 0).map((l) => displayAsset(l.asset));
  if (gave.length === 0 || got.length === 0) return null;
  return `${gave.join(' + ')} to ${got.join(' + ')}`;
}
export function AccountWords({ line }: { line: StatementLine }) {
  const own = lineAccount(line);
  return own ? <span className="truncate">{railWords(own)}</span> : <span>—</span>;
}

/** THE DESCRIPTION — short, counterpart-led, no amount inside (the Movement
 *  column has it), as the Wio statement writes it: one shape per kind, built
 *  from the row's structured fields. A party is a link to its own page, on
 *  its own desk (a between-parties move may cross desks).
 *
 *  Two forms of one wording. The table's (default): a flex line whose parts
 *  truncate, the whole sentence on the row's tooltip. `flow`: the phone
 *  card's — inline words that wrap, for a seat that clamps them itself. */
export function Description({ line, entity, flow = false }: { line: StatementLine; entity: string; flow?: boolean }) {
  const m = line.m;
  const who = (p: { slug: string; display_name: string; entity?: string }) => (
    <Link
      to={`/parties/${encodeURIComponent(p.slug)}?entity=${p.entity ?? entity}`}
      className={flow ? 'who hover:underline' : 'who hover:underline truncate'}
      title={p.display_name}
    >
      {partyName(p)}
    </Link>
  );
  let body: ReactNode;
  switch (m.kind) {
    // Money with no party came from, or went to, the outside: the account
    // leads ("Into Office cash"), never the Type pill's own word again.
    case 'money_in':
      body = isOpening(m)
        ? 'Opening balance'
        : m.party ? <>From {who(m.party)}</> : m.rail ? `Into ${railWords(m.rail)}` : '—';
      break;
    case 'money_out': {
      // The cost inside a payment — the commission, the network fee — is a
      // separate fact from the movement, so it is named after the
      // counterpart (" · angadiya commission"). Its figure stays out of the
      // description, as every figure does; the tooltip sentence carries it.
      const cost = m.cost ? ` · ${costWords(m.cost.category)}` : '';
      body = isOpening(m)
        ? 'Opening balance'
        : <>{m.party ? <>To {who(m.party)}</> : m.rail ? `Out of ${railWords(m.rail)}` : '—'}{cost}</>;
      break;
    }
    case 'trade':
      // A customer's trade is led by the customer. The desk's own conversion
      // has no party and says what became what, the way Wio writes an
      // exchange; when the legs cannot say, the account leads, as it does for
      // money with no party.
      body = m.party ? who(m.party) : swapWords(m) ?? (m.rail ? `On ${railWords(m.rail)}` : '—');
      break;
    case 'p2p':
      body = m.party
        ? (
          <>
            {who(m.party)}
            {m.counterparty && (
              <>
                {flow ? ' ' : null}
                <ArrowRight size={12} className={flow ? 'inline-block align-[-2px] opacity-70' : 'shrink-0 opacity-70'} />
                {flow ? ' ' : null}
                {who(m.counterparty)}
              </>
            )}
          </>
        )
        : '—';
      break;
    case 'rail_move': {
      // The OTHER account of the leg: the leg that gained money says where
      // it came from; every other line — the leg that paid, a leg that
      // netted to nothing because both sides were in scope, a row without
      // legs — says where it went, and the Account column names the payer.
      // When both sides are in scope the only figure left on the line is
      // the network fee the move lost, so the description says so: "To Hot
      // wallet · network fee" beside "−2 USDT".
      const feeOnly = !!m.cost && line.net !== null && line.net < 0
        && line.asset === foldAsset(m.asset)
        && tidy(Math.abs(line.net)) === tidy(Math.abs(m.cost.amount));
      if (line.net !== null && line.net > 0) body = m.rail ? `From ${railWords(m.rail)}` : '—';
      else if (m.counter_rail) body = `To ${railWords(m.counter_rail)}${feeOnly && m.cost ? ` · ${costWords(m.cost.category)}` : ''}`;
      else body = m.rail ? `From ${railWords(m.rail)}` : '—';
      break;
    }
    case 'expense':
      body = capitalise(costWords(m.cost?.category));
      break;
    case 'owner':
      body = m.direction === 'in' ? 'Owner put in' : 'Owner took out';
      break;
    case 'adjustment': {
      // Counterpart-led like the rest: an adjustment on a party's box names
      // the party, and the desk's profit share is named as what it is.
      const what = isOpening(m) ? 'Opening balance'
        : isProfitShare(m) ? 'Profit share'
        : m.direction === 'in' ? 'Wrote in' : 'Wrote off';
      body = m.party
        ? (flow ? <>{what} · {who(m.party)}</> : <><span className="shrink-0">{what} ·</span>{who(m.party)}</>)
        : what;
      break;
    }
    default:
      body = movementKindLabel(m.kind);
  }
  // The state, in a word ahead of the description, as the statement does;
  // the row's dimming only underlines it.
  const stateWord = m.state !== 'live'
    ? (
      <span className={`${flow ? '' : 'shrink-0 '}${m.state === 'reversal' ? 'text-warn' : 'text-tg-hint'}`}>
        {m.state === 'reversal' ? 'Reversal' : 'Reversed'} ·
      </span>
    )
    : null;
  if (flow) {
    return (
      <>
        {stateWord}
        {stateWord ? ' ' : null}
        {body}
      </>
    );
  }
  return (
    <div className="flex items-center gap-1 min-w-0">
      {stateWord}
      {typeof body === 'string' ? <span className="truncate">{body}</span> : body}
    </div>
  );
}

/** HOW MANY LINES A PHONE CARD GIVES THE DESCRIPTION. Two — but a between-parties
 *  row names TWO parties, and two long ones ("Kestrel Trading International LLC
 *  → Northern Gulf Commodities Trading") hid the second name behind the clamp's
 *  ellipsis (21 Oct 2026 audit): it gets a third line. A short pair still reads
 *  on one. Whole class names, so Tailwind finds them. */
export const descriptionClamp = (m: MovementLineRow): string => (m.kind === 'p2p' ? 'line-clamp-3' : 'line-clamp-2');

/** THE MOVEMENT FIGURE, one fact: "+1,000" (§5.14). With a leg, the sign is
 *  the leg's own — a trade's AED leg is + and its USDT leg −, whatever the
 *  row's headline direction. A move whose leg netted to nothing (both
 *  accounts in scope) still reads as a movement: the amount that crossed,
 *  unsigned and muted, neither in nor out. A row the Worker sent no legs for
 *  moved nothing on these accounts and says the amount that crossed the same
 *  way — and, on a between-parties swap, the return leg under it
 *  (crossedFigure); a dash only when it has no amount.
 *  Without the field at all — an older Worker — the row's own
 *  amount: the direction carries the sign, and a move wears no sign and the
 *  muted colour. The currency is the block's — its header names it. One
 *  piece for the table's cell and the card's line. */
export function MovementFigure({ line, code = false }: { line: StatementLine; code?: boolean }) {
  const m = line.m;
  const crossed = railless(m) ? crossedFigure(m) : null;
  // The currency, muted after the figure, WHERE NOTHING ELSE NAMES IT: a
  // currency block's header does, so the blocks leave it off; a journal row
  // carries every currency the entry moved and has no header, so it asks for
  // it — including on these unlegged forms, or one figure in the seat would
  // be the only one on the screen with no currency against it.
  const unit = code ? <> <span className="text-tg-hint">{displayAsset(m.asset)}</span></> : null;
  return line.net !== null
    ? (line.net === 0 && m.direction === 'move' && line.asset === foldAsset(m.asset)
      ? <span className="amt text-tg-hint">{fmtAmount(m.amount, m.asset)}{unit}</span>
      : <Delta value={line.net} asset={line.asset} code={code} />)
    : railless(m)
      ? (crossed
        ? (
          <span className="text-tg-hint">
            <span className="amt">{crossed.amount}{unit}</span>
            {crossed.back && <span className="block text-meta leading-snug">for {crossed.back}</span>}
          </span>
        )
        : <span className="text-tg-hint">—</span>)
      : m.direction === 'move'
        ? <span className="amt text-tg-hint">{fmtAmount(m.amount, m.asset)}{unit}</span>
        : <Delta value={m.direction === 'in' ? m.amount : -m.amount} asset={m.asset} code={code} />;
}

/** THE REFERENCE, short (shortReference): a deal opens its panel when the
 *  trades log's page holds it, else stands named; a receipt or payment
 *  reference copies on a click, as on the Statement. Nothing for a row with
 *  neither. One piece for the table's cell and the card's line. */
export function Reference({ m, onOpenDeal, muted = false }: {
  m: MovementLineRow;
  onOpenDeal: ((dealId: string) => void) | null;
  /** The phone card's: the reference in the hint colour — a door still,
   *  but not the loudest thing on a card of figures (20 Sept 2026). */
  muted?: boolean;
}) {
  if (m.deal_id) {
    return onOpenDeal
      ? (
        <button
          type="button"
          className={`${muted ? 'text-tg-hint hover:text-tg-text' : 'btn-link'} text-meta font-mono truncate min-w-0`}
          title={`${m.deal_id}\nOpen this deal`}
          onClick={() => onOpenDeal(m.deal_id!)}
        >
          {shortReference(m.deal_id)}
        </button>
      )
      : <span className="font-mono truncate min-w-0" title={m.deal_id}>{shortReference(m.deal_id)}</span>;
  }
  return m.reference ? <CopyRef value={m.reference} label={shortReference(m.reference)} /> : '';
}

/** ONE LINE AS A PHONE CARD (below lg). Two lines, always, on Ledger's card
 *  seats — the party statement's LegCard is the model: the type pill and
 *  the whole description (two lines of it at most), the account muted after
 *  it when the Account column would stand, and the reference at the right
 *  end — the way out of the card, on every card, whether or not a balance
 *  stands (a deal opens its panel, a receipt or payment copies on a click,
 *  as in the table's Reference column); then the time alone (the card sits
 *  under a day label, so the day is not said twice — and the full stamp left
 *  no room for the Reverse word beside the desk's widest figures at 375px),
 *  the movement, the bare Reverse word in the middle where the door is on
 *  and the line can be reversed, and at the right the balance where the
 *  Balance column would stand. The BFF's sentence and the note ride on the
 *  card's tooltip, as on the row; the reverse panel opens beneath the card. */
export function MovementCard({ line, showAccount, showBalance, showReverse, onOpenDeal, entity, onReversed }: {
  entity: string;
  line: StatementLine;
  showAccount: boolean;
  showBalance: boolean;
  showReverse: boolean;
  onOpenDeal: ((dealId: string) => void) | null;
  onReversed: (o: ReversedOutcome) => void;
}) {
  const m = line.m;
  const title = m.notes ? `${m.words}\n${m.notes}` : m.words;
  const reversible = showReverse && canReverse(m);
  const [reverseOpen, setReverseOpen] = useState(false);
  const account = showAccount ? lineAccount(line) : null;
  return (
    <div className={`${LEDGER.card} px-2 ${m.state === 'live' ? '' : LEDGER.rowDim}`} title={title}>
      <div className={LEDGER.cardLine1}>
        <KindPill label={movementKindLabel(m.kind)} className="shrink-0 mt-px" />
        {/* A between-parties row names TWO parties: under the pill and the reference, the whole card's
            width, so the receiving name is not the one that is cut (Astra review, 21 Oct 2026). */}
        {m.kind === 'p2p'
          ? <span className="flex-1" />
          : (
            <span className={`flex-1 min-w-0 ${descriptionClamp(m)} break-words`}>
              <Description line={line} entity={entity} flow />
              {account && <span className="text-tg-hint"> · {railWords(account)}</span>}
            </span>
          )}
        {/* The reference never takes more than 40% of the line (22 Oct 2026 stress: a 24-character hand-typed one is shown
            whole, and left the description "From Northe…"); it ends in an ellipsis like the table's Reference column, and
            the whole of it is what a tap copies. */}
        <span className="flex items-center shrink-0 max-w-[40%] mt-px text-meta text-tg-hint">
          <Reference m={m} onOpenDeal={onOpenDeal} muted />
        </span>
      </div>
      {m.kind === 'p2p' && (
        <div className="mt-1 min-w-0 line-clamp-4 break-words text-body leading-snug">
          <Description line={line} entity={entity} flow />
        </div>
      )}
      {/* Line 2 on fixed seats: the time, the movement, then the Reverse
          word against the balance's own seat — so it stands on ONE x on
          every card (it used to float at the line's middle, at a different
          x on every row — operator, 20 Sept 2026: "the same vertical line"). */}
      <div className={LEDGER.cardLine2}>
        <span className={`${LEDGER.whenText} shrink-0`}>{fmtTime(m.at)}</span>
        <span className="whitespace-nowrap shrink-0"><MovementFigure line={line} /></span>
        <span className="ml-auto shrink-0">
          {reversible && (
            <ReverseEntryButton short plain open={reverseOpen} onClick={() => setReverseOpen((v) => !v)} />
          )}
        </span>
        {showBalance && (
          <span className="w-[6.5rem] shrink-0 text-right whitespace-nowrap">
            {line.running !== null && <Figure value={line.running} asset={line.asset} />}
          </span>
        )}
      </div>
      {reversible && reverseOpen && (
        <ReverseEntryPanel
          entry={m}
          onClose={() => setReverseOpen(false)}
          onReversed={onReversed}
          className="mt-2"
        />
      )}
    </div>
  );
}

export function MovementLine({ line, showAccount, showBalance, showReverse, cols, onOpenDeal, entity, onReversed }: {
  /** The desk this ledger was read for. A party link opens on the party's OWN
   *  desk (the row says which); this is the fallback for a row that does not. */
  entity: string;
  line: StatementLine;
  /** The Account column stands unless the account filter names one account. */
  showAccount: boolean;
  /** The Balance column stands once the Worker sends `legs`. */
  showBalance: boolean;
  /** The Reverse seat and its panel — off on a glance. */
  showReverse: boolean;
  /** How many columns the row spans — the reverse panel sits across them. */
  cols: number;
  /** null when there is no deal on this row, or when the trades log's page
   *  does not reach back far enough to hold it. */
  onOpenDeal: ((dealId: string) => void) | null;
  /** The screen seats the outcome above the table (see the header). */
  onReversed: (o: ReversedOutcome) => void;
}) {
  const m = line.m;
  // ONE LINE PER ENTRY AND CURRENCY. The BFF's sentence — "paid 8,06,970 INR
  // to Thanos from Angadiya cash" — is the Description, Movement and Account
  // cells read left to right, so the row does not print it as well. It rides
  // on the row's tooltip with the note, for the one time the words are
  // wanted. The reference is a column of its own: a deal opens its panel, a
  // receipt or payment copies on a click. Who booked it is not on the row —
  // one operator, and "tg:…" is an internal identifier (§7).
  const title = m.notes ? `${m.words}\n${m.notes}` : m.words;
  // THE DOOR (17 Sept 2026): a live money row can be reversed from here; a
  // trade's leg cannot — its deal opens instead, and Void is in the panel.
  const reversible = showReverse && canReverse(m);
  const [reverseOpen, setReverseOpen] = useState(false);
  const td = LEDGER.td;

  return (
    <>
    <tr
      className={`border-b border-tg-secondary lg:border-stroke-soft last:border-b-0 ${m.state === 'live' ? '' : LEDGER.rowDim} ${reverseOpen ? 'border-b-0' : ''}`}
      title={title}
    >
      {/* The TIME alone: the day is the separator row above, and "Y 20:02"
          under a Yesterday heading said the day twice. The party statement and
          the phone cards already print the time and nothing else. */}
      <td className={`${td} ${LEDGER.when}`}>{fmtTime(m.at)}</td>
      {/* The pill's own column, one width on every row — so the description
          starts on one vertical line. The pill names the kind, not the
          state: a row that is not live says so in a word ahead of the
          description, as the statement does; the dimming only underlines it. */}
      <td className={`${td} ${LEDGER.kind}`}>
        <KindPill label={movementKindLabel(m.kind)} />
      </td>
      <td className={`${td} ${LEDGER.words}`}>
        <Description line={line} entity={entity} />
      </td>
      {showAccount && (
        <td className={`${td} ${ACCOUNT} text-tg-hint`}>
          <div className="flex items-center gap-1 min-w-0">
            <AccountWords line={line} />
          </div>
        </td>
      )}
      {/* One fact, one cell: "+1,000" (§5.14) — MovementFigure says how. */}
      <td className={`${td} ${LEDGER.fig}`}>
        <MovementFigure line={line} />
      </td>
      {/* The balance after this entry, over the accounts in scope — the
          Wio statement's last column. Blank on a railless row. */}
      {showBalance && (
        <td className={`${td} ${LEDGER.fig}`}>
          {line.running !== null && <Figure value={line.running} asset={line.asset} />}
        </td>
      )}
      {/* THE WAY OUT OF THE ROW. What moved is here; what was agreed is in
          the deal, and the operator should not have to go and find it by
          hand in the trades log. A row with no deal carries its own
          reference, which copies on a click, as on the Statement. */}
      <td className={`${td} ${REF} overflow-hidden`}>
        <span className="flex items-center gap-2 min-w-0">
          <Reference m={m} onOpenDeal={onOpenDeal} />
        </span>
      </td>
      {/* The door's own seat, on every row — empty where the row cannot be
          reversed — so it sits at one x down the block. */}
      {showReverse && (
        <td className={`${td} ${LEDGER.act}`}>
          {reversible && (
            <ReverseEntryButton short open={reverseOpen} onClick={() => setReverseOpen((v) => !v)} />
          )}
        </td>
      )}
      <td className={LEDGER.slack} aria-hidden="true" />
    </tr>
    {/* The panel, beneath the row, across every column. */}
    {reversible && reverseOpen && (
      <tr className="border-b border-tg-secondary lg:border-stroke-soft">
        <td colSpan={cols} className="px-2 pb-2 pt-0">
          <ReverseEntryPanel
            entry={m}
            onClose={() => setReverseOpen(false)}
            onReversed={onReversed}
          />
        </td>
      </tr>
    )}
    </>
  );
}

/* ══════════════════ THE JOURNAL — ONE ROW PER TRANSACTION ══════════════════ */

// Operator, 21 Sept 2026, on Home: "we are having entries multiple times and
// repeating patterns bcoz trades cause money movements as well" — asked
// whether trades and money could be one table. Ruled the same day: "worth it
// — but not everywhere", the party page AND Home; Money movements (the
// bank-statement view for reconciling) and the Trades log (the deal register)
// stay as they are. The party page shipped 22 Sept; this is Home's half.
//
// The blocks above stand a transaction ONCE PER CURRENCY, which is the
// repetition he was looking at: one spot trade is a row in the AED block and
// a row in the USDT block, under a Recent trades card that had already listed
// it a third time. The journal keeps every seat and every door and regroups
// the same lines onto their transaction — a trade is ONE row with a line per
// currency inside the Account, Movement and Balance seats, a receipt one
// line. No new read, no new field: `linesOf` already folds a row's legs by
// currency, and one row of the feed already IS one transaction.
//
// THE SHAPE IS THE PARTY JOURNAL'S, for the reason that journal was reshaped
// on 22 Sept ("maybe u copied the looks from legacy admin?"): the bank
// statement's seats, the currency muted on each figure, never a column per
// currency. What differs is what the figures MEAN. On a party page Movement
// and Balance are that party's box; here they are the desk's own accounts —
// the same accounts, and the same `legs`, the blocks above read.
//
// A TRADE THE DESK HAS NOT SETTLED MOVED NO ACCOUNT OF ITS OWN. A settle-later
// trade leaves both legs on the party (trade_unsettled), so the Worker's
// `legs` — treasury accounts only — come back empty and the row would draw a
// dash where the deal belongs. The deal is on this screen already (the trades
// log's page, `dealsById`), so such a row draws the DEAL's two legs, muted, as
// what was agreed and has not moved: the figures the accounts will carry when
// it settles, in the colour that says they have not yet. Its Balance stays
// blank, because no balance of the desk's changed.

/** One transaction, with a line for each currency it moved. `lines` are
 *  `linesOf`'s — newest first is the feed's own order, so the rows need no
 *  sort of their own. */
export interface JournalEntry {
  key: string;
  m: MovementLineRow;
  lines: StatementLine[];
  /** The lines are the deal's, not the accounts': agreed, not yet moved. */
  agreed: boolean;
  /** The deal's own lifecycle, set only with `agreed` — what the Account seat
   *  says in place of an account no money moved on (agreedAccountWords). */
  lifecycle?: string | null;
}

/** WHAT THE ACCOUNT SEAT SAYS WHEN NO ACCOUNT MOVED. An agreed row's lines are
 *  the deal's, so there is no account to name and the seat drew a dash twice.
 *  A deal still waiting is "Settle later" — the screen's own name for it. Any
 *  other state is the deal panel's own word ("Settled", "Carried in"): the
 *  sweep closes a settle-later trade through separate entries while its own
 *  posting still moves nothing, and that row must not read as open. Null when
 *  the deal's state is not known — the dash stays, because the seat claims
 *  nothing it cannot read. This READS the state to word a label; it gates
 *  nothing (withAbsentDeals's note on lifecycle is about admission). */
export function agreedAccountWords(lifecycle: string | null | undefined): string | null {
  if (!lifecycle) return null;
  return lifecycle === 'awaiting' ? 'Settle later' : lifecycleLabel(lifecycle);
}

/** A TRADE'S RATE, SMALL UNDER ITS PARTY on Home's journal (operator, 4 Oct
 *  2026: "keep 12 rows", the rate visible without a column — most rows are
 *  money movements with no rate, so a column would stand blank on them).
 *  Worded as the deal rows word it: "@ 3.64", the quote currency read from
 *  the deal (rateQuote) at that currency's own places (fmtRate). Never for a
 *  cheque, whose rate says nothing; never for a row with no deal in hand or
 *  no stored rate — a row that cannot say the rate says nothing. */
export function tradeRateWords(m: MovementLineRow, dealsById: Map<string, TradeRow>): string | null {
  if (m.kind !== 'trade' || !m.deal_id) return null;
  const d = dealsById.get(m.deal_id);
  if (!d || isChequeDeal(d)) return null;
  const rate = fmtRate(d.rate, rateQuote(d));
  return rate === '—' ? null : `@ ${rate}`;
}

/** THE DESK'S SIDE OF A DEAL. The customer pays `take` — the desk's holding
 *  of that asset goes up — and receives `give`, which goes down. True of a
 *  buy and a sell alike: the verb is the customer's, the sign is the desk's,
 *  and these are the same two figures the accounts will carry once the trade
 *  settles. */
function dealLines(m: MovementLineRow, t: TradeRow): StatementLine[] {
  const take = foldAsset(t.asset_take), give = foldAsset(t.asset_give);
  const net = new Map<string, number>();
  net.set(take, tidy(Number(t.amount_take)));
  net.set(give, tidy((net.get(give) ?? 0) - Number(t.amount_give)));
  return [...net.entries()].map(([asset, v]) => ({
    key: `${m.txn_id}|${asset}`, m, asset, net: v, running: null,
  }));
}

/** The feed's rows as journal entries — one per transaction, in the feed's
 *  order. A trade with no account leg of its own borrows the deal's. */
export function journalEntries(rows: MovementLineRow[], dealsById: Map<string, TradeRow>): JournalEntry[] {
  // ROWS THAT SHARE `linked_deal_id` GROUP INTO ONE ROW (30 Sept 2026, on a
  // sub-broker trade the operator saw as three rows on Home — the trade's
  // own posting, always unsettled so it carries no account of its own, plus
  // a money-in and a delivery-out, each its own transaction). withAbsentDeals()
  // already keeps the synthetic placeholder OFF the feed once one real row
  // carries the deal's id (its own `onFeed` check), so what reaches here for
  // such a deal is the two real legs, each its own txn_id — this is what
  // folds them into the one row that already exists for an ordinary trade's
  // two currency legs. A transfer linking itself to a deal this way is
  // refused unless the deal exists (bff/src/routes/transfers.ts), so
  // grouping by it is never a guess at two unrelated postings sharing a
  // coincidence.
  //
  // THE GROUPING KEY IS `linked_deal_id`, NEVER BARE `deal_id` (1 Oct 2026 —
  // caught by Bookkeeping the same day `linked_deal_id` shipped, against a
  // pattern that predates this journal entirely). `deal_id` alone is not
  // safe to group multiple rows by: bff/routes/manager-cheque.ts stamps the
  // SAME deal_id on the customer's deposit AND the seller's payment — two
  // different parties, one deal, by MC's own design (stage 1 line ~421,
  // stage 2 line ~584). Grouping on bare deal_id merged those into one row
  // naming only whichever party's leg sorted first — a real bug this
  // session shipped and Astra caught before it reached main. `linked_deal_id`
  // carries no such history: Mechanisms scoped it to exactly one thing, one
  // party's money legs of one sub-broker trade, so it alone is safe to fold
  // multiple rows by. A row that carries only `deal_id` (an ordinary trade's
  // own settled posting, or one side of an MC) never merges with anything
  // here — it groups by its own txn_id, same as before this feature existed.
  //
  // `deal_id` still gets ONE job: naming which deal a row's Reference opens,
  // resolved as `deal_id ?? linked_deal_id` on every row whether or not it
  // ever merges — see `m` below. That resolution never leaks into a
  // component the Reverse door reads (this journal has none of its own;
  // Home is a glance, the door stays on Money movements) and never decides
  // what groups with what.
  //
  // newest-first in, so the FIRST row seen for a key is already the latest
  // — later rows for the same key contribute only their lines, and the
  // group reads as a Trade (the pill, the party-only description) rather
  // than whichever leg's own "Money in"/"Money out" sentence was seen last.
  const byKey = new Map<string, JournalEntry>();
  for (const row of rows) {
    // The synthetic placeholder for a deal genuinely absent from the feed
    // (txn_id: `deal:${dealId}`, see withAbsentDeals) is never grouped —
    // its own guard already guarantees nothing else on the feed shares its
    // key, so this is always a fresh entry, and it stays `agreed: true`.
    // withAbsentDeals() always stamps this row's OWN deal_id (never
    // linked_deal_id), so no fold is needed to find it.
    const synthetic = row.txn_id.startsWith('deal:') && row.deal_id ? dealsById.get(row.deal_id) : undefined;
    if (synthetic) {
      byKey.set(row.deal_id!, {
        key: row.deal_id!, m: row, lines: dealLines(row, synthetic), agreed: true, lifecycle: synthetic.lifecycle ?? null,
      });
      continue;
    }
    const resolvedDeal = row.deal_id ?? row.linked_deal_id ?? null;
    const m = resolvedDeal && !row.deal_id ? { ...row, deal_id: resolvedDeal } : row;
    // `linked_deal_id` alone decides the group — see the doc comment above
    // for why bare `deal_id` cannot.
    const key = row.linked_deal_id ?? m.txn_id;
    const lines = linesOf(m);
    const existing = byKey.get(key);
    if (existing) {
      existing.lines = existing.lines.concat(lines);
      if (existing.m.kind !== 'trade') existing.m = { ...existing.m, kind: 'trade' };
    } else {
      byKey.set(key, { key, m, lines, agreed: false });
    }
  }
  return [...byKey.values()];
}

/** THE MOVEMENTS FEED DOES NOT CARRY A SETTLE-LATER TRADE, and a journal built
 *  on it alone would lose the deals this desk most needs to see.
 *
 *  The feed's admission rule is the accounts: a transaction is on it because it
 *  MOVED one (bff/src/lib/movements.ts — "TRADES MOVE RAILS AND SO TRADES ARE
 *  HERE"). `trade_unsettled` moves none — both legs stay on the party — so it
 *  is excluded there by the same rule that admits the settled ones, and it is
 *  excluded deliberately: Money movements is the bank-statement view, and a
 *  deal that moved no money has no place on it. That is right for Money and
 *  wrong for Home, which is a glance at what the desk DID.
 *
 *  So Home's journal is the feed PLUS the recent deals the feed does not
 *  carry, deduplicated by deal — a trade that moved accounts arrives as a
 *  movement and is not added again, which is the whole point of the journal.
 *  A deal added this way draws its own two legs, muted, with no balance: it is
 *  what was agreed, and no account of the desk's has moved for it yet. A
 *  VOIDED deal is left out; the void is its own event and Home is a glance,
 *  not the deal register.
 *
 *  Found by the Astra review, 24 Sept 2026, on a first cut that read the feed
 *  alone — and independently by the CEX session's own review the same hour.
 *  Do not "simplify" this back to one read.
 *
 *  `feed` IS UNDEFINED WHEN THE MOVEMENTS READ HAS NOT ANSWERED, and then
 *  nothing is folded in at all. "Absent from the feed" is only a fact about a
 *  deal when the feed was actually read; with the read failed, EVERY deal
 *  looks absent, and a settled spot trade — which did move two accounts —
 *  would be drawn as agreed-and-not-yet-moved, with no account and no
 *  balance. That is a failed read rendering as a real answer, which is the one
 *  thing this card must never do; the warning line above it says the feed is
 *  missing instead. (Second Astra review, 24 Sept 2026.)
 *
 *  KNOWN RESIDUAL, AND WHY IT IS NOT FIXED HERE (fourth Astra review, 24 Sept
 *  2026). Absence from the feed is evidence, not proof. The two reads are
 *  independent snapshots, so a spot trade booked in the gap between them — the
 *  movements read completes, the trade is booked, the deals read completes —
 *  is genuinely absent from that feed page although it did move two accounts,
 *  and is drawn agreed until the next refetch of movements corrects it (both
 *  queries refetch on window focus, so it corrects itself). The window is
 *  seconds and the row is never wrong about a BALANCE — it shows none.
 *
 *  Closing it properly needs something only the Worker can say: whether a
 *  deal's own booking transaction touched a treasury account. `DealRow`
 *  carries no such field, and inventing one on the console would be guessing
 *  at the posting shape from the outside — which is the whole class of error
 *  this journal has already been bitten by twice. That is a question for the
 *  Mechanisms session (a field on the deals route, or a Home feed that admits
 *  unsettled bookings), not for a screen. Until then, do not try to infer it
 *  from lifecycle or from timestamps: a timestamp gate ("only fold in deals
 *  older than the newest feed row") reintroduces the original bug, because a
 *  settle-later trade booked after the last movement is exactly the row this
 *  function exists to keep.
 *
 *  Note what is deliberately NOT gated: the deal's lifecycle. A trade booked
 *  settle-later and closed later by the sweep reads 'settled' while its own
 *  transaction still moved no account — its money went through separate
 *  entries, which are on the feed under their own references. Folding it in by
 *  absence keeps it on the card; gating on lifecycle === 'awaiting' would make
 *  it vanish the moment it settled, which is the bug this function exists to
 *  fix, one step later.
 */
export function withAbsentDeals(
  feed: MovementLineRow[] | undefined,
  deals: DealRow[],
  limit: number,
): MovementLineRow[] {
  if (!feed) return [];
  const rows = feed;
  // A row LINKED to a deal without being its own leg (a sub-broker's money
  // legs, api.transfer's linked_deal_id) is just as much "this deal is on
  // the feed" as a trade's own deal_id is — otherwise a sub-broker trade
  // whose money has posted would still get the synthetic placeholder added
  // back on top of its two real legs, the exact bug this fixed 30 Sept.
  const onFeed = new Set(
    rows.flatMap((m) => [m.deal_id, m.linked_deal_id]).filter(Boolean) as string[],
  );
  const extra: MovementLineRow[] = deals
    .filter((d) => d.lifecycle !== 'voided' && !onFeed.has(d.deal_id))
    .map((d) => ({
      // Prefixed so it can never collide with a transaction id — this row is
      // a DEAL, and nothing about it is a ledger transaction.
      txn_id: `deal:${d.deal_id}`,
      reference: d.deal_id,
      at: d.booked_at,
      kind: 'trade' as const,
      // The Worker writes the sentence for a movement; a deal has none, and
      // one composed here would be a second way of saying what the books
      // already say. The row's seats carry it, and the deal panel has the
      // rest; only the operator's own note rides the tooltip.
      words: '',
      party: d.party_slug
        ? { slug: d.party_slug, display_name: d.party_name ?? d.party_slug, party_type: d.party_type ?? 'customer' }
        : null,
      counterparty: null, rail: null, counter_rail: null, other_rail: null,
      asset: d.asset_take, amount: Number(d.amount_take),
      direction: 'in' as const, cost: null, notes: d.notes ?? null,
      operator: null, state: 'live' as const, legs: [], deal_id: d.deal_id,
    }));
  if (extra.length === 0) return rows.slice(0, limit);
  return [...rows, ...extra]
    .sort((a, b) => (a.at === b.at ? 0 : a.at < b.at ? 1 : -1))
    .slice(0, limit);
}

/** A figure that has been AGREED and has not moved: the sign law's shape in
 *  the hint colour, never the negative red — nothing has left the desk yet.
 *  The row's tooltip carries the Worker's own sentence, which ends
 *  "— settle later". */
function AgreedDelta({ value, asset, code }: { value: number; asset: string; code: boolean }) {
  if (value === 0) return <span className="text-tg-hint">—</span>;
  return (
    <span className="whitespace-nowrap text-tg-hint">
      <span className="amt">{value > 0 ? '+' : '−'}{fmtAmount(Math.abs(value), asset)}</span>
      {code && <> <span>{displayAsset(asset)}</span></>}
    </span>
  );
}

/** One currency's movement inside a journal row — the accounts' own figure,
 *  or the deal's where there is no account leg at all. `code` prints the
 *  currency muted on the figure: the table's seat holds every currency and
 *  needs it, the phone card gives the code a seat of its own and would say it
 *  twice.
 *
 *  EVERY ACCOUNT LINE GOES THROUGH MovementFigure, never Delta directly. A
 *  move between two accounts that are BOTH in scope nets to zero in its
 *  currency, and Delta draws a zero as a dash — so 5,00,000 AED crossing from
 *  Wio to Office cash would have shown the operator no figure at all. Only
 *  MovementFigure knows that case (it prints the amount that crossed, unsigned
 *  and muted, counting toward neither side). Found by the Astra review, 24
 *  Sept 2026. */
function JournalMovement({ line, agreed, code }: { line: StatementLine; agreed: boolean; code: boolean }) {
  if (agreed) return <AgreedDelta value={line.net ?? 0} asset={line.asset} code={code} />;
  return <MovementFigure line={line} code={code} />;
}

/** THE JOURNAL — the feed's rows, one row each, under their day rows. The
 *  caller owns the scroller, the empty state and the paging, as it does for
 *  the blocks. No Reverse seat: the journal is Home's glance and the door
 *  stays on Money movements, where the entry is listed with its own
 *  reference. */
export function MovementJournal({
  rows, entity, showAccount, showBalance, dealsById, onOpenDeal, compact = false, refs = 'all',
}: {
  rows: MovementLineRow[];
  entity: string;
  showAccount: boolean;
  showBalance: boolean;
  dealsById: Map<string, TradeRow>;
  onOpenDeal: (dealId: string) => void;
  compact?: boolean;
  /** Which rows carry a Reference: every one (the party page), or only a DEAL's —
   *  the door to its panel. Home's glance says "deals": a receipt's grey RCPT code
   *  there is not a door to anything (21 Oct 2026 audit). */
  refs?: 'all' | 'deals';
}) {
  const entries = useMemo(() => journalEntries(rows, dealsById), [rows, dealsById]);
  const cols = 4 + (showAccount ? 1 : 0) + (showBalance ? 1 : 0) + 2;
  const dealDoor = (m: MovementLineRow) => (m.deal_id && dealsById.has(m.deal_id) ? onOpenDeal : null);
  if (entries.length === 0) return null;

  return (
    <div className={compact ? '' : LEDGER.block}>
      {/* lg+: the table, on the ledger's own seats. */}
      <div className="hidden lg:block overflow-x-auto">
        <table className={`${LEDGER.table} min-w-[880px]`}>
          <thead>
            <tr>
              <th className={`${LEDGER.th} ${LEDGER.whenW}`}>When</th>
              <th className={`${LEDGER.th} ${LEDGER.kind}`}>Type</th>
              <th className={`${LEDGER.th} ${LEDGER.words}`}>Description</th>
              {showAccount && <th className={`${LEDGER.th} ${ACCOUNT}`}>Account</th>}
              <th className={`${LEDGER.th} ${LEDGER.figWide}`}>Movement</th>
              {showBalance && <th className={`${LEDGER.th} ${LEDGER.figWide}`}>Balance</th>}
              <th className={`${LEDGER.th} ${REF}`}>Reference</th>
              <th className={LEDGER.slack} aria-hidden="true" />
            </tr>
          </thead>
          <tbody>
            {entries.map((e, i) => {
              const prev = entries[i - 1];
              const newDay = !prev || dayKey(prev.m) !== dayKey(e.m);
              return (
                <Fragment key={e.key}>
                  {newDay && <DayRow label={dayLabel(e.m)} colSpan={cols} />}
                  <JournalRow
                    entry={e}
                    entity={entity}
                    showAccount={showAccount}
                    showBalance={showBalance}
                    rate={tradeRateWords(e.m, dealsById)}
                    onOpenDeal={dealDoor(e.m)}
                    refs={refs}
                  />
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Below lg: a card per transaction. */}
      <div className="lg:hidden">
        {entries.map((e, i) => {
          const prev = entries[i - 1];
          const newDay = !prev || dayKey(prev.m) !== dayKey(e.m);
          return (
            <Fragment key={e.key}>
              {newDay && <div className={LEDGER.day}>{dayLabel(e.m)}</div>}
              <JournalEntryCard
                entry={e}
                entity={entity}
                showAccount={showAccount}
                showBalance={showBalance}
                rate={tradeRateWords(e.m, dealsById)}
                onOpenDeal={dealDoor(e.m)}
                refs={refs}
              />
            </Fragment>
          );
        })}
      </div>
    </div>
  );
}

/** ONE TRANSACTION AS A ROW. The When, Type, Description and Reference seats
 *  carry the transaction — one of each, however many currencies it moved —
 *  and the Account, Movement and Balance seats carry a line per currency,
 *  stacked, so a trade's two currencies read down inside its own row instead
 *  of standing as two rows in two blocks. */
function JournalRow({ entry, entity, showAccount, showBalance, rate, onOpenDeal, refs }: {
  entry: JournalEntry;
  entity: string;
  showAccount: boolean;
  showBalance: boolean;
  /** "@ 3.64" under the party on a trade, or null (tradeRateWords). */
  rate: string | null;
  onOpenDeal: ((dealId: string) => void) | null;
  refs: 'all' | 'deals';
}) {
  const { m, lines, agreed } = entry;
  const stateWords = agreed ? agreedAccountWords(entry.lifecycle) : null;
  const title = m.notes ? `${m.words}\n${m.notes}` : m.words;
  const td = LEDGER.td;
  // ALWAYS entry.m, NEVER lines[0].m (30 Sept 2026). A grouped row's lines
  // keep each source transaction's own `.m` — a sub-broker's money-in and
  // delivery-out each need THEIRS for AccountWords to name the right
  // account per currency, below — but the row's own sentence is about the
  // GROUP, which is `entry.m` once journalEntries() has flipped its kind to
  // 'trade'. `lines[0].m` would still be whichever leg happened to sort
  // first — "To Loki OTC" from the delivery leg, not the trade's own
  // party-only reading — the Type pill (which already reads entry.m) and
  // the Description would have disagreed about what kind of row this is.
  const lead: StatementLine = { key: m.txn_id, m, asset: foldAsset(m.asset), net: null, running: null };
  return (
    <tr
      className={`border-b border-tg-secondary lg:border-stroke-soft last:border-b-0 ${m.state === 'live' ? '' : LEDGER.rowDim}`}
      title={title}
    >
      <td className={`${td} ${LEDGER.when}`}>{fmtTime(m.at)}</td>
      <td className={`${td} ${LEDGER.kind}`}>
        <KindPill label={movementKindLabel(m.kind)} />
      </td>
      {/* The sentence is the GROUP's, from entry.m — see lead, above. A
          trade's rate rides under it, muted, on the same line height as the
          figure lines beside it, so the two lines of a two-currency row read
          straight across. */}
      <td className={`${td} ${LEDGER.words}${rate ? ' leading-snug' : ''}`}>
        <Description line={lead} entity={entity} />
        {rate && <div className="text-tg-hint truncate">{rate}</div>}
      </td>
      {showAccount && (
        <td className={`${td} ${ACCOUNT} text-tg-hint align-top`}>
          {/* An agreed row moved no account: the seat says the deal's state
              once, on its first line, instead of a dash per currency. */}
          {lines.map((l, i) => (
            <div key={l.asset} className="flex items-center gap-1 min-w-0 leading-snug">
              {stateWords
                ? (i === 0 ? <span className="truncate">{stateWords}</span> : null)
                : <AccountWords line={l} />}
            </div>
          ))}
        </td>
      )}
      <td className={`${td} ${LEDGER.figWide} align-top`}>
        {lines.map((l) => (
          <div key={l.asset} className="leading-snug"><JournalMovement line={l} agreed={agreed} code /></div>
        ))}
      </td>
      {showBalance && (
        <td className={`${td} ${LEDGER.figWide} align-top`}>
          {lines.map((l) => (
            <div key={l.asset} className="leading-snug">
              {l.running !== null && (
                <>
                  <Figure value={l.running} asset={l.asset} />{' '}
                  <span className="text-tg-hint">{displayAsset(l.asset)}</span>
                </>
              )}
            </div>
          ))}
        </td>
      )}
      <td className={`${td} ${REF} overflow-hidden align-top`}>
        <span className="flex items-center gap-2 min-w-0">
          {(refs === 'all' || m.deal_id) && <Reference m={m} onOpenDeal={onOpenDeal} />}
        </span>
      </td>
      <td className={LEDGER.slack} aria-hidden="true" />
    </tr>
  );
}

/** ONE TRANSACTION AS A PHONE CARD — the party journal's card, seat for seat:
 *  the type pill and the sentence, the reference at the line's right end;
 *  then the time; then a line per currency — the code, the movement, the
 *  balance — so the figures read down one edge whether the entry moved one
 *  currency or two. */
function JournalEntryCard({ entry, entity, showAccount, showBalance, rate, onOpenDeal, refs }: {
  entry: JournalEntry;
  entity: string;
  refs: 'all' | 'deals';
  /** "@ 3.64" beside the time on a trade, or null (tradeRateWords). */
  rate: string | null;
  /** The account each currency moved on, muted beside its code. A card that
   *  drops it cannot say WHICH account a receipt reached — the description
   *  names only the party (the card that stood here before this journal
   *  appended it after the description, which a trade's two accounts would
   *  have made a lie). Astra review, 24 Sept 2026. */
  showAccount: boolean;
  showBalance: boolean;
  onOpenDeal: ((dealId: string) => void) | null;
}) {
  const { m, lines, agreed } = entry;
  const stateWords = agreed ? agreedAccountWords(entry.lifecycle) : null;
  const title = m.notes ? `${m.words}\n${m.notes}` : m.words;
  // Always entry.m — see JournalRow's lead for why lines[0].m is wrong here.
  const lead: StatementLine = { key: m.txn_id, m, asset: foldAsset(m.asset), net: null, running: null };
  return (
    <div className={`${LEDGER.card} px-2 ${m.state === 'live' ? '' : LEDGER.rowDim}`} title={title}>
      <div className={LEDGER.cardLine1}>
        <KindPill label={movementKindLabel(m.kind)} className="shrink-0 mt-px" />
        {m.kind === 'p2p'
          ? <span className="flex-1" />
          : (
            <span className={`flex-1 min-w-0 ${descriptionClamp(m)} break-words`}>
              <Description line={lead} entity={entity} flow />
            </span>
          )}
        <span className="flex items-center shrink-0 max-w-[40%] mt-px text-meta text-tg-hint">
          {(refs === 'all' || m.deal_id) && <Reference m={m} onOpenDeal={onOpenDeal} muted />}
        </span>
      </div>
      {m.kind === 'p2p' && (
        <div className="mt-1 min-w-0 line-clamp-4 break-words text-body leading-snug">
          <Description line={lead} entity={entity} flow />
        </div>
      )}
      <div className={LEDGER.cardLine2}>
        <span className={`${LEDGER.whenText} shrink-0`}>{fmtTime(m.at)}</span>
        {rate && <span className="text-tg-hint">{rate}</span>}
      </div>
      <div className="mt-0.5 space-y-0.5 text-meta">
        {lines.map((l, i) => {
          const account = showAccount ? lineAccount(l) : null;
          // An agreed row moved no account: its state rides the first line's
          // account slot (see the table's Account seat).
          const state = showAccount && i === 0 ? stateWords : null;
          return (
            <div key={l.asset} className="flex items-baseline gap-2">
              <span className="w-12 shrink-0 text-tg-hint text-meta">{displayAsset(l.asset)}</span>
              <span className="whitespace-nowrap"><JournalMovement line={l} agreed={agreed} code={false} /></span>
              {account && (
                <span className="flex-1 min-w-0 truncate text-tg-hint text-meta">{railWords(account)}</span>
              )}
              {!account && state && (
                <span className="flex-1 min-w-0 truncate text-tg-hint text-meta">{state}</span>
              )}
              {!account && !state && <span className="flex-1" />}
              {showBalance && (
                <span className="whitespace-nowrap shrink-0">
                  {l.running !== null && <Figure value={l.running} asset={l.asset} />}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
